import XmlViewer from "@components/fileViewers/xmlViewer/XmlViewer";
import { ISelectionChangeArgs } from "@components/inputs/select/Select.types";
import { TToolbarItem } from "@components/toolbar/Toolbar.types";
import { EntityTypeName } from "@odata/GeneratedEntityTypes";
import { IAppContext } from "contexts/appContext/AppContext.types";
import { TFunction } from "i18next";
import React from "react";
import { WithTranslation } from "react-i18next";

import { MimeType, ToolbarItemType } from "../../enums";
import {
    fileNameToMime,
    isFileImage,
    isFileIsDoc,
    isFilePdf,
    isFilePlainText,
    isFileXml
} from "../fileUploader/File.utils";
import ImgViewer from "./imgViewer/ImgViewer";
import NoPreview from "./NoPreview";
import PDFViewer from "./pdfViewer/PDFViewer";
import TextViewer from "./textViewer/TextViewer";

export interface IFileViewerProps {
    src: string;
    fileName?: string;
    isReadOnly?: boolean;
    hiddenItems?: FileToolbarItem[];
    // items that need to be explicitly passed to be shown
    forcedVisibleItems?: FileToolbarItem[];
    isRemoveDisabled?: boolean;
    onFileRemove?: (src: string) => void;
    onGoToList?: () => void;
    customFileActions?: TToolbarItem[];
    onCustomFileAction?: (actionId: string) => void;
    onChatCreate?: (args: ISelectionChangeArgs) => void;
    hasCustomMessages?: boolean;
    context?: IAppContext;
    id?: number;
}

const getVisibleItem = (item: TToolbarItem, isVisible: boolean) => {
    return isVisible ? [item] : [];
};

export enum FileToolbarItem {
    Dashboard = "Dashboard",
    PreviousPage = "PreviousPage",
    NextPage = "NextPage",
    PageCount = "PageCount",
    PointerType = "PointerType",
    ZoomIn = "ZoomIn",
    ZoomOut = "ZoomOut",
    Zoom = "Zoom",
    Fit = "Fit",
    Note = "Note",
    Download = "Download",
    Chat = "Chat",
    Remove = "Remove",
    Print = "Print"
}

export const getSharedToolbarDefinition = (args: IFileViewerProps & WithTranslation):
    {
        staticItems: TToolbarItem[],
        items: TToolbarItem[]
    } => {
    return {
        staticItems: [
            ...getVisibleItem({
                id: FileToolbarItem.Dashboard,
                label: args.t("Components:Pdf.BackToList"),
                itemType: ToolbarItemType.Icon,
                iconName: "Arrow"
            }, !args.hiddenItems.includes(FileToolbarItem.Dashboard))
        ],
        items: [
            ...getVisibleItem({
                id: FileToolbarItem.Chat,
                label: args.t("Components:FileUploader.Download"),
                itemType: ToolbarItemType.ChatUserSelect,
                itemProps: {
                    iconName: "ChatFilled",
                    hasCustomMessages: args.hasCustomMessages,
                    context: args.context,
                    entityType: EntityTypeName.InboxFile,
                    documentId: args.id,
                    selectProps: {
                        onChange: args.onChatCreate
                    }
                },
                iconName: "Chat"
            }, args.forcedVisibleItems?.includes(FileToolbarItem.Chat)),

            ...getVisibleItem({
                id: FileToolbarItem.Download,
                label: args.t("Components:FileUploader.Download"),
                itemType: ToolbarItemType.Icon,
                iconName: "Download"
            }, !args.hiddenItems.includes(FileToolbarItem.Download)),
            ...getVisibleItem({
                id: FileToolbarItem.Remove,
                label: args.t("Components:FileUploader.Delete"),
                itemType: ToolbarItemType.Icon,
                iconName: "Bin",
                isDisabled: args.isRemoveDisabled
            }, !args.isReadOnly && !args.hiddenItems.includes(FileToolbarItem.Remove))
        ]
    };
};

export const getFileNameFromSrc = (src: string): string => {
    return src.split("/").slice(-1)[0];
};

export function isFileOpenable(name: string): boolean {
    const mimeType = fileNameToMime(name);
    return isFilePdf(mimeType) || isFileImage(mimeType) || isFilePlainText(mimeType) /*|| isFileIsDoc(name) || isFileXml(mimeType)*/;
}

export function getFileViewer(mime: MimeType, props: IFileViewerProps): React.ReactElement {
    let viewer;

    if (isFilePdf(mime)) {
        viewer = <PDFViewer {...props} />;
    } else if (isFilePlainText(mime)) {
        viewer = <TextViewer {...props}/>;
    } else if (isFileImage(mime)) {
        viewer = <ImgViewer {...props}/>;
    } else if (isFileIsDoc(props.fileName) || isFileXml(mime)) {
        viewer = <XmlViewer {...props}/>;

    } else {
        // cannot be opened - illustration and possibility to download
        viewer = <NoPreview isEmpty={!props.src} {...props}/>;
    }

    return viewer;
}
